import React, { useEffect, useState } from 'react';
import { colourHex } from '../../lib/format.ts';
import type { PlanOutput, PlannedSlide } from '../../../shared/deck.ts';
import { ChevronDown, ChevronUp, Trash, X } from '../../lib/icons.tsx';

type PlanReviewProps = {
  planPhase: string;
  plan: PlanOutput | null;
  busy?: boolean;
  errors?: string[] | null;
  onBack?: () => void;
  onApprove: (slides: PlannedSlide[]) => void;
  /** Regenerate the whole plan from a free-text note. Awaited and tested for
   *  truthiness: truthy clears the note field.
   *
   *  NOTHING passes this today, so the "Adjust the whole plan" block it gates
   *  never renders — dormant rather than broken, but the doc comment above
   *  still describes it as a live feature. */
  onReplan?: (note: string) => boolean | Promise<boolean>;
  onClose?: () => void;
  /** Copy overrides so the New Section modal reads about a section, not a
   *  whole deck. Defaults preserve the New Deck wording. */
  title?: string;
  planningTitle?: string;
  approveLabel?: string;
  intro?: React.ReactNode;
};

/**
 * The plan-review screen inside a modal — the hard gate before any slide is
 * written. Reorder, delete or retitle slides, then "Build this deck" resumes
 * the build from the reviewed plan. "Adjust" regenerates the whole plan from a
 * free-text note. The slide TYPE is shown read-only here (a dropdown to swap it
 * lands in the next slice; the build agent already overrides an unfit type).
 *
 * Shared by the New Deck modal (`NewDeckModal`) and the New Section modal
 * (`SectionModal`) — both render it inside their own `.modal--new-deck` shell.
 */
export default function PlanReview({
  planPhase,
  plan,
  busy,
  errors,
  onBack,
  onApprove,
  onReplan,
  onClose,
  // Optional copy overrides so the New Section modal reads about a section, not
  // a whole deck. Defaults preserve the New Deck wording.
  title = 'Review the plan',
  planningTitle = 'Planning your deck…',
  approveLabel = 'Build this deck',
  intro,
}: PlanReviewProps) {
  const [slides, setSlides] = useState<PlannedSlide[]>(() => (plan?.slides || []).map((s) => ({ ...s })));
  useEffect(() => {
    setSlides((plan?.slides || []).map((s) => ({ ...s })));
  }, [plan]);
  const [replanNote, setReplanNote] = useState('');

  const move = (i: number, d: number) =>
    setSlides((arr) => {
      const j = i + d;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      const a = next[i];
      const b = next[j];
      if (a === undefined || b === undefined) return arr;
      next[i] = b;
      next[j] = a;
      return next;
    });
  const remove = (i: number) => setSlides((arr) => arr.filter((_, idx) => idx !== i));
  const edit = (i: number, patch: Partial<PlannedSlide>) =>
    setSlides((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  // Planning, or a degraded build with no plan yet: a simple busy state.
  if (planPhase === 'planning' || !plan) {
    return (
      <>
        <header className="modal__header new-deck__head">
          <h2 className="modal__title">{planningTitle}</h2>
          <span className="add-slide__spacer" />
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Cancel" title="Cancel">
            <X />
          </button>
        </header>
        <div className="modal__body new-deck__body new-deck__planning">
          <span className="new-deck__spinner" aria-hidden="true" />
          <p className="muted">
            Reading your brief and laying out every slide. A short brief takes a few seconds; a long, detailed one can
            take a minute or two, hang tight.
          </p>
        </div>
        <footer className="modal__footer new-deck__foot">
          <button type="button" className="btn btn--ghost" onClick={onBack}>
            Cancel
          </button>
        </footer>
      </>
    );
  }

  let lastSection: string | null = null;
  return (
    <>
      <header className="modal__header new-deck__head">
        <h2 className="modal__title">{title}</h2>
        <span className="add-slide__spacer" />
        <button type="button" className="iconbtn" onClick={onClose} aria-label="Cancel" title="Cancel">
          <X />
        </button>
      </header>
      <div className="modal__body new-deck__body">
        <p className="muted small" style={{ margin: 0 }}>
          {intro || (
            <>
              {plan.slides.length} content slides across {plan.sections.length} section
              {plan.sections.length === 1 ? '' : 's'}: cover, dividers and closing are added automatically. Reorder,
              delete or rename before it builds.
            </>
          )}
        </p>
        {errors && errors.length > 0 && (
          <div className="plan-panel__errors">
            {errors.map((e, i) => (
              <div key={i}>• {e}</div>
            ))}
          </div>
        )}
        <div className="new-deck__plan">
          {slides.map((s, i) => {
            const section = plan.sections.find((x) => x.key === s.section);
            const header =
              s.section !== lastSection ? (
                <div key={'h' + s.section + i} className="plan-panel__section">
                  <span className="plan-panel__dot" style={{ background: colourHex(section?.color || 'ORANGE') }} />
                  {section?.name || s.section}
                </div>
              ) : null;
            lastSection = s.section;
            return (
              <React.Fragment key={s.filename + i}>
                {header}
                <div className="plan-panel__row">
                  <button
                    type="button"
                    className="plan-panel__rowbtn iconbtn"
                    aria-label="Move up"
                    title="Move up"
                    disabled={busy}
                    onClick={() => move(i, -1)}
                  >
                    <ChevronUp />
                  </button>
                  <button
                    type="button"
                    className="plan-panel__rowbtn iconbtn"
                    aria-label="Move down"
                    title="Move down"
                    disabled={busy}
                    onClick={() => move(i, 1)}
                  >
                    <ChevronDown />
                  </button>
                  <span className="plan-panel__row-title">
                    <input value={s.title} disabled={busy} onChange={(e) => edit(i, { title: e.target.value })} />
                  </span>
                  <span className="plan-panel__row-prim">{s.primitive || s.template}</span>
                  <button
                    type="button"
                    className="plan-panel__rowbtn iconbtn"
                    aria-label="Delete slide"
                    title="Delete slide"
                    disabled={busy}
                    onClick={() => remove(i)}
                  >
                    <Trash />
                  </button>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        {plan.rationale && <p className="plan-panel__rationale">{plan.rationale}</p>}
        {onReplan && (
          <div className="new-deck__replan">
            <span className="settings__heading">Something off? Adjust the whole plan</span>
            <textarea
              className="new-deck__notes"
              rows={2}
              value={replanNote}
              disabled={busy}
              placeholder='e.g. "Add a slide on pricing near the end" or "make section two about retention"'
              onChange={(e) => setReplanNote(e.target.value)}
            />
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={busy || !replanNote.trim()}
              onClick={async () => {
                if (await onReplan(replanNote.trim())) setReplanNote('');
              }}
            >
              {busy ? 'Re-planning…' : 'Re-plan with this'}
            </button>
          </div>
        )}
      </div>
      <footer className="modal__footer new-deck__foot">
        <span className="muted small new-deck__foot-hint">No slides are written until you build.</span>
        <button type="button" className="btn btn--ghost" onClick={onBack} disabled={busy}>
          Back to options
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || slides.length === 0}
          onClick={() => onApprove(slides)}
        >
          {busy ? 'Starting…' : approveLabel}
        </button>
      </footer>
    </>
  );
}
